import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useAuth } from '../../../hooks/useAuth';
import OnboardingWelcomeModal from './OnboardingWelcomeModal';
import OnboardingCompletionModal from './OnboardingCompletionModal';

const OnboardingTrigger = () => {
  const { hasCompletedOnboarding, isOnboardingActive, isSampleDataLoaded, exitSampleData } = useOnboarding();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Immediately hide welcome modal when onboarding is completed
  // This ensures the modal closes right away when user clicks "Skip"
  const shouldShowWelcomeModal = showWelcomeModal && !hasCompletedOnboarding;

  // Show completion modal when sample data is loaded but onboarding steps are not active (only for logged in users)
  const showCompletionModal = isAuthenticated && isSampleDataLoaded && !isOnboardingActive;

  useEffect(() => {
    // Don't show onboarding for unauthenticated users
    if (!isAuthenticated) {
      setShowWelcomeModal(false);
      return;
    }

    // Hide welcome modal if completion modal is showing
    if (showCompletionModal) {
      setShowWelcomeModal(false);
      return;
    }

    // Hide welcome modal if user has completed onboarding or is currently in onboarding
    if (hasCompletedOnboarding || isOnboardingActive || isSampleDataLoaded) {
      setShowWelcomeModal(false);
      return;
    }

    // Auto-trigger: Show welcome modal for authenticated users who haven't completed onboarding
    const timer = setTimeout(() => {
      setShowWelcomeModal(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [hasCompletedOnboarding, isOnboardingActive, showCompletionModal, isSampleDataLoaded, isAuthenticated, location.pathname]);

  const handleCloseWelcomeModal = () => {
    // When user closes the modal (X button or Skip button), mark onboarding as skipped
    setShowWelcomeModal(false);
  };

  const handleCloseCompletionModal = () => {
    exitSampleData();
  };

  return (
    <>
      <OnboardingWelcomeModal
        open={shouldShowWelcomeModal}
        onClose={handleCloseWelcomeModal}
      />
      <OnboardingCompletionModal
        open={showCompletionModal}
        onClose={handleCloseCompletionModal}
      />
    </>
  );
};

export default OnboardingTrigger;
